import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, Button, IconButton, InputLabel, Stack, TextField } from '@mui/material';
import { lowerCase } from 'lodash';
import { useId, useState } from 'react';

import { Text, isDefined, isTruthy } from 'common';

import { type ReactFlowInputParameterConfig, createParameterConfig } from './parameterConfig';
import { useReactFlowViewContext } from '../../../context/context';
import { BlockTextField } from '../../ConfigFields/TextField';
import { type ConfigFieldProps } from '../../ConfigFields/types';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';

const entityIdNameByType: Record<string, string> = {
  Patient: 'ehrInformationId',
  Encounter: 'encounterId',
  Order: 'patientOrderId',
  ExternalDocumentPacket: 'externalDocumentPacketId',
};

interface FlowParametersProps extends ConfigFieldProps {
  entityType?: string;
}

export function FlowParameters(props: FlowParametersProps) {
  const { fieldName, entityType } = props;

  // Name of an implicit parameter due to entity type (e.g., "encounterId")
  const entityIdName = entityType && entityIdNameByType[entityType];

  const { setValue, value, visibleError } = useSelectedNodeField<
    ReactFlowInputParameterConfig[] | undefined
  >(fieldName);
  const { isEditable } = useReactFlowViewContext();
  // Auto-focus on the last row if it was just added
  const [hasAdded, setHasAdded] = useState(false);

  const onAddRow = () => {
    setValue([...(value ?? []), createParameterConfig()]);
    setHasAdded(true);
  };
  const onDeleteRow = (index: number) => {
    setHasAdded(false);
    setValue(value && [...value.slice(0, index), ...value.slice(index + 1)]);
  };

  const labelId = useId();
  const hasExistingParameters = isTruthy(value?.length || entityIdName);

  return (
    <Stack gap={0.5}>
      <InputLabel id={labelId}>Inputs</InputLabel>
      {!hasExistingParameters ? (
        <Box>
          <Button variant="outlined" onClick={() => onAddRow()} disabled={!isEditable}>
            + Add input
          </Button>
        </Box>
      ) : (
        <Box aria-labelledby={labelId}>
          <Box>
            <Stack direction="row" gap={1}>
              <Text variant="heading6" bold flex={2} padding="6.5px 14px">
                Name
              </Text>
              <Text variant="heading6" bold flex={3} padding="6.5px 14px">
                Description
              </Text>
              <Box width={52} />
            </Stack>
            {isDefined(entityIdName) && !value?.some(({ name }) => name === entityIdName) && (
              // If there's an implicit entity parameter, and no explicit parameter
              // has the same name, include that as a first non-editable row
              <Stack direction="row" gap={1}>
                <Box flex={2}>
                  <TextField
                    multiline
                    size="small"
                    fullWidth
                    disabled={true}
                    value={entityIdName}
                    slotProps={{
                      htmlInput: {
                        'aria-label': `Input ${entityIdName}`,
                      },
                    }}
                  />
                </Box>
                <Box flex={3}>
                  <TextField
                    multiline
                    size="small"
                    fullWidth
                    disabled={true}
                    value={`ID of the Notable ${lowerCase(entityType)}`}
                    slotProps={{
                      htmlInput: {
                        'aria-label': `Input ${entityIdName} description`,
                      },
                    }}
                  />
                </Box>
                <Box>
                  <IconButton tabIndex={-1} disabled={true}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </Box>
              </Stack>
            )}
            {value?.map(({ id }, index) => (
              <Stack direction="row" gap={1} key={id}>
                <Box flex={2}>
                  <BlockTextField
                    fieldName={`${fieldName}.${index}.name`}
                    aria-label={`Input ${index + 1} name`}
                    placeholder="Name"
                    multiline
                    size="small"
                    autoFocus={hasAdded && index === value.length - 1}
                  />
                </Box>
                <Box flex={3}>
                  <BlockTextField
                    fieldName={`${fieldName}.${index}.description`}
                    aria-label={`Input ${index + 1} description`}
                    placeholder="Description"
                    multiline
                    size="small"
                  />
                </Box>
                <Box>
                  <IconButton
                    onClick={() => onDeleteRow(index)}
                    disabled={!isEditable}
                    aria-label={`Delete input ${index + 1}`}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Box>
              </Stack>
            ))}
          </Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Text variant="subtitle2" color="error.main" bold>
              {visibleError ?? ''}
            </Text>
            <Button onClick={() => onAddRow()} disabled={!isEditable}>
              + Add input
            </Button>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
